import dynamic from "next/dynamic";

import {
  Block,
  BLOCKS,
  Document,
  Heading2,
  Hyperlink,
  Inline,
  Node,
  Text,
  TopLevelBlock,
} from "@contentful/rich-text-types";

import BlogImage from "@/components/blog/BlogImage";
import ResourcesCTA from "@/components/ResourcesCTA";
import { slugify } from "@/lib/utils";
import { HeaderItem } from "@/types/blog-post";

import type { Options } from "@contentful/rich-text-react-renderer";

const ReactPlayer = dynamic(() => import("react-player"), { ssr: false });
const BlogVideo = dynamic(() => import("@/components/blog/BlogVideo"), {
  ssr: false,
});

// check if a node has a youtube link anywhere in its children (or their children)
const hasYouTubeLink = (node: Node): boolean => {
  // base case: if node is a hyperlink with youtube url
  if (node.nodeType === "hyperlink") {
    const url = (node as Hyperlink).data.uri;
    return /youtube\.com\/watch\?v=/.test(url) || /youtu\.be\//.test(url);
  }

  // recursive case: check content array
  if ("content" in node && Array.isArray(node.content)) {
    return node.content.some((child: Node) => hasYouTubeLink(child));
  }

  return false;
};

let lastHeadingContent = "";

export const richTextOptions: Options = {
  renderNode: {
    "heading-2": (node, children) => {
      const plainText = getPlainTextFromHeader(node.content);

      return <h2 id={slugify(plainText)}>{children}</h2>;
    },
    "list-item": (node, children) => {
      return <li className="not-prose">{children}</li>;
    },
    "heading-3": (node, children) => {
      const plainText = getPlainTextFromHeader(node.content);
      lastHeadingContent = plainText;

      if (lastHeadingContent.toLowerCase().includes("key") && lastHeadingContent.toLowerCase().includes("takeaways")) {
        return <h3 className="pt-12 border-t-2 border-t-mint-500">{children}</h3>;
      }

      return <h3>{children}</h3>;
    },
    "unordered-list": (node, children) => {
      if (lastHeadingContent.toLowerCase().includes("key") && lastHeadingContent.toLowerCase().includes("takeaways")) {
        lastHeadingContent = "";
        return (
          <ul className="pb-12 border-b-2 border-b-mint-500 flex flex-col gap-4 leading-[1.4] font-medium text-ash-700">
            {children}
          </ul>
        );
      }
      return <ul>{children}</ul>;
    },
    hyperlink: (node, children) => {
      const url = node.data.uri;
      const isYouTubeUrl = /youtube\.com\/watch\?v=/.test(url) || /youtu\.be\//.test(url);
      if (isYouTubeUrl) {
        return (
          <BlogVideo>
            <ReactPlayer src={url} playing={false} controls />
          </BlogVideo>
        );
      }

      return (
        <a target="_blank" rel="noreferrer noopener" href={url}>
          {children}
        </a>
      );
    },
    "embedded-entry-block": ({ data }) => {
      const caption = data?.target?.fields?.caption;
      const imageUrl = data?.target?.fields?.image?.fields?.file?.url;

      if (imageUrl) {
        return <BlogImage src={imageUrl} alt={caption} caption={caption} />;
      } else {
        return <div>Embedded entry block not accounted for...</div>;
      }
    },
    [BLOCKS.PARAGRAPH]: (node, children) => {
      // react-player is wrapped in a div, so wrapping it in a p tag will
      // result in invalidDOMNesting error
      if (hasYouTubeLink(node)) {
        return <>{children}</>;
      }

      return <p>{children}</p>;
    },
    "cta-node": () => {
      return <ResourcesCTA />;
    },
  },
};

export function getPlainTextFromHeader(contentNode: Array<Inline | Text | Block>): string {
  const result = contentNode.reduce((acc, current) => {
    // handle hyperlinks by extracting text from their content
    if (current.nodeType === "hyperlink") {
      const hyperlink = current as Hyperlink;
      const linkText = hyperlink.content.reduce((linkAcc, linkNode) => {
        return linkAcc + linkNode.value;
      }, "");
      return acc + linkText;
    }
    // handle regular text nodes
    if (current.nodeType === "text") {
      return acc + (current.value || "");
    }
    // For any other node types, just return the accumulated text
    return acc;
  }, "");

  return result;
}

export function getHeadersFromRichText(richText: Document): HeaderItem[] {
  return richText.content
    .filter((content) => content.nodeType === BLOCKS.HEADING_2)
    .map((heading) => {
      const plainText = getPlainTextFromHeader((heading as Heading2).content);

      return {
        text: plainText,
        href: `#${slugify(plainText)}`,
      };
    });
}

export function formatImageUrl(url: string): string {
  // Check if the URL starts with "//"
  if (url.startsWith("//")) {
    // Prepend 'https:' to the URL
    return "https:" + url;
  }
  // Return the original URL if it's already absolute
  return url;
}

export const insertCTANode = (documentTree: Document, afterParagraphIndex: number): Document => {
  if (!documentTree?.content || !Array.isArray(documentTree.content)) {
    console.warn("Invalid document tree structure");
    return documentTree;
  }

  if (typeof afterParagraphIndex !== "number" || afterParagraphIndex < 0) {
    console.warn("Invalid paragraph index");
    return documentTree;
  }

  let paragraphCount = 0;
  const newContent: TopLevelBlock[] = [];

  documentTree.content.forEach((node: TopLevelBlock) => {
    newContent.push(node);
    if (node?.nodeType === BLOCKS.PARAGRAPH) {
      paragraphCount++;

      if (paragraphCount === afterParagraphIndex) {
        newContent.push({ nodeType: "cta-node", content: [] } as unknown as TopLevelBlock);
      }
    }
  });

  return { ...documentTree, content: newContent };
};
